import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

interface EnhancedHttpErrorResponse extends HttpErrorResponse {
  isFinalError?: boolean;
  totalAttempts?: number;
}

@Injectable()
export class HttpRetryInterceptor implements HttpInterceptor {
  private maxRetries = 2;
  private retryDelay = 2000; // 2 second
  private retryStatusCodes = [500, 502, 503, 504, 0];

  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return this.makeRequest(request, next, 0);
  }

  private makeRequest<T>(
    request: HttpRequest<T>,
    next: HttpHandler,
    attempt: number
  ): Observable<HttpEvent<T>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Should we retry this error?
        if (this.shouldRetry(error, attempt)) {
          const delay = this.retryDelay * (attempt + 1);

          console.log(
            `Request failed, retrying in ${delay}ms (attempt ${attempt + 1}/${this.maxRetries})`
          );

          // Wait and retry
          return timer(delay).pipe(mergeMap(() => this.makeRequest(request, next, attempt + 1)));
        }

        // No more retries - log and throw error
        // Create new error with final error marker
        const finalError: EnhancedHttpErrorResponse = Object.assign(error, {
          isFinalError: true,
          totalAttempts: attempt + 1,
        });
        return throwError(() => finalError);
      })
    );
  }

  private shouldRetry(error: HttpErrorResponse, attempt: number): boolean {
    // Don't retry if we've hit max attempts
    if (attempt >= this.maxRetries) {
      return false;
    }

    // Only retry specific error codes
    return this.retryStatusCodes.includes(error.status);
  }
}
